<script setup lang="ts">
    import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { IChatMessage } from '../../services/api/chat/chat';
    import { useAskMeStore } from '../../stores/ask-me';
    import { __ } from '../../stores/expressions';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import { ArrowUpIcon, InfoIcon } from '@lucide/vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { AskMeModulePermissionFlag, PermissionArea } from '@/js/consts/permissions';
    import LoadingDots from '../LoadingDots.vue';

    const props = defineProps<{
        module?: Boolean;
        filterStoreName: FilterStoreName;
    }>();

    const question = ref('');
    const input = ref<HTMLTextAreaElement | null>(null);
    const route = useRoute();
    const ask_me = useAskMeStore();
    const router = useRouter();
    const { hasPermission } = usePermissions();

    // Guards createNewChat().then() below: it can resolve after this widget closes and
    // must not then overwrite whatever chat is active by then.
    let isMounted = true;
    onUnmounted(() => {
        isMounted = false;
    });

    watch(
        () => route.params.chat,
        (chat) => {
            question.value = '';
            nextTick(() => {
                if (input.value) {
                    input.value.focus();
                }
            });
        },
        { immediate: true }
    );

    const send = () => {
        if (ask_me.loading || question.value.trim() === '') {
            return;
        }

        const message: IChatMessage = {
            id: '_',
            messageType: 'Text',
            isUserMessage: true,
            message: question.value,
        };

        ask_me.addMessageToCurrentChat(message);
        question.value = '';

        if (props.module) {
            if (!ask_me.currentChatId) {
                ask_me.createNewChat(message, props.filterStoreName).then((response) => {
                    if (!isMounted) return;
                    ask_me.updateCurrentChatId(response.data.id);
                });
            } else {
                ask_me.sendChatMessage(ask_me.currentChatId, message.message as string);
            }
        } else if (!route.params.chat || route.params.chat === 'new-chat') {
            ask_me.createNewChat(message, props.filterStoreName).then((response) => {
                router.push(`/ask-me/${response.data.id}`);
            });
        } else {
            ask_me.sendChatMessage(route.params.chat as string, message.message as string);
        }
    };

    const hasAskMeAccess = hasPermission(
        PermissionArea.AskMeModule,
        AskMeModulePermissionFlag.Access
    );

    const wrapperClasses = computed(() => {
        if (props.module) {
            return 'relative flex-shrink-0 z-20 px-4 pb-4 flex flex-col';
        } else {
            let classes = [];

            classes.push('flex-shrink-0 absolute z-20 px-10 2xl:px-20 pb-5');

            if (ask_me.messages.length === 0) {
                classes.push('top-1/2 w-200 max-w-full left-1/2 -translate-1/2 px-10');
            } else {
                classes.push('bottom-0 left-0 right-0 pointer-events-none');
            }

            return classes.join(' ');
        }
    });
</script>

<template>
    <div v-if="hasAskMeAccess" :class="wrapperClasses">
        <p
            v-if="!module && ask_me.messages.length === 0"
            aria-hidden="true"
            class="text-[24px] text-center w-full font-medium mb-10 text-gray-dark"
        >
            Need help?
            <span class="block text-[40px] font-bold font-stolzl text-primary-beet">
                Just Ask Me.
            </span>
        </p>

        <div
            v-if="ask_me.loading"
            class="z-50 relative flex justify-start pb-3 pointer-events-auto"
        >
            <LoadingDots :color="false" />
        </div>

        <form
            class="relative z-10 rounded-xl overflow-hidden shadow-sm pointer-events-auto"
            @submit.prevent="send()"
        >
            <textarea
                ref="input"
                v-model="question"
                class="text-base text-input h-20 border border-gray-lightest rounded-xl focus:ring-0 focus:outline-0 resize-none"
                :aria-label="__('write-your-message')"
                @keydown.enter.exact.prevent="send()"
            />

            <button
                :disabled="ask_me.loading"
                type="submit"
                class="absolute bottom-3 right-3 p-1 rounded-full cursor-pointer"
                :aria-label="__('send-message')"
            >
                <span
                    class="w-8 h-8 flex items-center justify-center border border-gray-light rounded-full"
                >
                    <ArrowUpIcon class="size-5 text-gray-medium" />
                </span>
            </button>
        </form>

        <div
            v-if="!module"
            class="absolute z-0 bg-gradient-to-t from-white from-15% to-transparent left-0 right-4 bottom-0 h-46 pointer-events-none"
        />

        <p
            class="relative pt-2 z-20 flex items-start gap-1.5 text-gray-medium text-caption pointer-events-auto"
        >
            <InfoIcon :size="16" class="w-4 shrink-0" />
            {{ __('ask-me-answers-disclaimer') }}
        </p>
    </div>
</template>
